import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../../api/countries/get-countries-list'
import { getNationalitiesList } from '../../../../api/countries/get-nationalities-list'
import useMultipleSchema from '../../../../lib/hooks/use-multiple-schema'
import useTranslate from '../../../../lib/hooks/use-translate'
import { GenderEnum, genderOneLetterMapping } from '../../../../lib/types/enums/common/gender.enum'
import { IDTypeEnum, IDTypeEnumMapping } from '../../../../lib/types/enums/common/id-type.enum'
import { getCountryLabel } from '../../../../lib/types/models/country/country-list'
import { EnrollmentIdentityInput } from '../../../../lib/types/models/enrollment/enrollment-identity'
import Form, {
  FormControl,
  FormDescription,
  FormField,
  FormItem
} from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledRadioInput from '../../../elements/inputs/controlled-radio-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import LoadingScreen from '../../../elements/loading/loading-screen'
import {
  SAVE_FOR_LATER_ENROLLMENT_IDENTITY_FORM_SCHEMA_VALIDATION,
  SUBMISSION_ENROLLMENT_IDENTITY_FORM_SCHEMA_VALIDATION
} from './client-space-enrollment.utils'

type EnrollmentIdentityFormProps = {
  defaultValues?: Partial<EnrollmentIdentityInput>
  requiredDocuments?: IDTypeEnum[]
  disabled?: boolean
  handleSubmit: (data: EnrollmentIdentityInput) => Promise<void>
}

export function EnrollmentIdentityForm({
  requiredDocuments,
  disabled,
  defaultValues,
  handleSubmit
}: EnrollmentIdentityFormProps) {
  const { t, currentLanguage } = useTranslate()

  const { form, loading, submitForm, formState } = useMultipleSchema<
    ['save-for-later', 'submit'],
    EnrollmentIdentityInput
  >(
    [
      {
        name: 'save-for-later',
        schema: SAVE_FOR_LATER_ENROLLMENT_IDENTITY_FORM_SCHEMA_VALIDATION
      },
      { name: 'submit', schema: SUBMISSION_ENROLLMENT_IDENTITY_FORM_SCHEMA_VALIDATION }
    ],
    async (values, name) =>
      handleSubmit({ ...values, childIdentityPapersSubmitted: name === 'submit' }),
    defaultValues
  )

  const { data: nationalities, isPending: isPendingNationalities } = useQuery({
    queryKey: ['nationalities'],
    queryFn: getNationalitiesList
  })

  const { data: countryList, isPending: isPendingCountryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList()
  })

  const identityDocumentType = form.watch('identityDocumentType')
  const identityDocumentNumber = form.watch('identityDocumentNumber')
  const identityDocumentIssuedAt = form.watch('identityDocumentIssuedAt')
  const identityDocumentIssuingCountry = form.watch('identityDocumentIssuingCountry')
  const identityDocumentExpiration = form.watch('identityDocumentExpiration')

  const isIdentityDocumentPassport = identityDocumentType === IDTypeEnum.PASSPORT

  useEffect(() => {
    if (formState !== 'submit') return

    if (identityDocumentNumber) form.clearErrors('identityDocumentNumber')
    if (identityDocumentIssuedAt) form.clearErrors('identityDocumentIssuedAt')
    if (identityDocumentExpiration) form.clearErrors('identityDocumentExpiration')

    if (identityDocumentIssuingCountry) form.clearErrors('identityDocumentIssuingCountry')
  }, [
    form,
    formState,
    identityDocumentNumber,
    identityDocumentIssuedAt,
    identityDocumentExpiration,
    identityDocumentIssuingCountry
  ])

  function resetIdentityDocumentFields() {
    form.setValue('identityDocumentNumber', null)
    form.setValue('identityDocumentIssuedAt', null)
    form.setValue('identityDocumentExpiration', null)
    form.setValue('identityDocumentIssuingCountry', null)
    form.clearErrors()
  }

  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues])

  if (isPendingNationalities || isPendingCountryList) return <LoadingScreen />

  return (
    <Form form={form}>
      <form>
        <Box
          className={
            'grid mobile:grid-cols-1 grid-cols-2 gap-4 border border-grey-light rounded-lg p-4'
          }
        >
          <FormField
            control={form.control}
            name={'childLastName'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput
                    {...field}
                    required
                    type={'text'}
                    label={'Nom'}
                    disabled={disabled}
                    tooltipContent={
                      <img
                        src={'/passport-name.png'}
                        alt={'Passport name'}
                        className={'w-[36rem] max-w-full rounded-lg'}
                      />
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'childFirstName'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput
                    {...field}
                    required
                    type={'text'}
                    label={'Prénom'}
                    disabled={disabled}
                    tooltipContent={
                      <img
                        src={'/passport-first-name.png'}
                        alt={'Passport first name'}
                        className={'w-[36rem] max-w-full rounded-lg'}
                      />
                    }
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'childBirthDate'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    required
                    mode="dynamic"
                    label={'Date de naissance'}
                    disabledFutureDates
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className={'flex flex-row gap-1 items-center'}>
            <Typography variant={'tag'}>Sexe* :</Typography>
            <div className={'flex gap-5'}>
              {Object.values(GenderEnum).map((gender) => (
                <FormField
                  key={gender}
                  control={form.control}
                  name={'childGender'}
                  render={({ field }) => {
                    return (
                      <FormItem>
                        <FormControl>
                          <ControlledRadioInput
                            {...field}
                            label={genderOneLetterMapping[gender].label}
                            checked={field.value === gender}
                            onCheck={() => field.onChange(gender)}
                            disabled={disabled}
                          />
                        </FormControl>
                      </FormItem>
                    )
                  }}
                />
              ))}
            </div>
          </div>

          <FormField
            control={form.control}
            name={'childNationality'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    required
                    label={'Nationalité'}
                    searchable
                    tooltipContent={
                      <img
                        src={'/passport-nationality.png'}
                        alt={'Passport nationality'}
                        className={'w-[36rem] max-w-full rounded-lg'}
                      />
                    }
                    options={nationalities?.map((nationality) => ({
                      label: nationality.label,
                      value: nationality.code
                    }))}
                    selectedOption={conditionalRender(!!field.value, {
                      label:
                        nationalities?.find((nationality) => nationality.code === field.value)
                          ?.label ?? '',
                      value: field.value
                    })}
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'residenceCountry'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    searchable
                    label={'Pays de résidence'}
                    selectedOption={conditionalRender(!!field.value, {
                      label:
                        getCountryLabel(
                          currentLanguage,
                          countryList?.data.find((c) => c.key.toString() === field.value)
                        ) ?? '',
                      value: field.value
                    })}
                    options={
                      countryList?.data.map((c) => ({
                        label: getCountryLabel(currentLanguage, c) ?? '',
                        value: c.key
                      })) ?? []
                    }
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          {requiredDocuments && requiredDocuments.length > 0 && (
            <>
              <div className={'mobile:col-span-1 col-span-2 flex flex-row gap-7'}>
                <Typography variant={'tag'}>{`Document ${
                  requiredDocuments.length === 1
                    ? t(IDTypeEnumMapping[requiredDocuments[0]].title)
                    : ''
                }`}</Typography>

                {requiredDocuments.length > 1 &&
                  requiredDocuments.map((documentType) => (
                    <FormField
                      key={documentType}
                      control={form.control}
                      name={'identityDocumentType'}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <ControlledRadioInput
                              {...field}
                              label={t(IDTypeEnumMapping[documentType].title)}
                              checked={field.value === documentType}
                              onCheck={() => {
                                field.onChange(documentType)
                                resetIdentityDocumentFields()
                              }}
                              disabled={disabled}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  ))}
              </div>

              <FormField
                control={form.control}
                name={'identityDocumentNumber'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        type={'text'}
                        label={isIdentityDocumentPassport ? 'N° de passeport' : 'N° Document'}
                        disabled={disabled}
                        tooltipContent={
                          <img
                            src={isIdentityDocumentPassport ? '/passport-number.png' : '/id.png'}
                            alt={'Passport number'}
                            className={'w-[36rem] max-w-full rounded-lg'}
                          />
                        }
                      />
                    </FormControl>
                    <FormDescription>
                      {isIdentityDocumentPassport && (
                        <Typography variant={'label'} color={tailwindColors.grey.DEFAULT}>
                          NB : le numéro d'un passeport francais se compose de 2 chiffres, suivis
                          de 2 lettres puis de 5 chiffres
                        </Typography>
                      )}
                    </FormDescription>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'identityDocumentIssuedAt'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={'Date de délivrance'}
                        disabledFutureDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'identityDocumentIssuingCountry'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledSelectInput
                        {...field}
                        searchable
                        label={'Pays émetteur'}
                        selectedOption={conditionalRender(!!field.value, {
                          label:
                            getCountryLabel(
                              currentLanguage,
                              countryList?.data.find((c) => c.key.toString() === field.value)
                            ) ?? '',
                          value: field.value
                        })}
                        options={
                          countryList?.data.map((c) => ({
                            label: getCountryLabel(currentLanguage, c) ?? '',
                            value: c.key
                          })) ?? []
                        }
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'identityDocumentExpiration'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledDateStringInput
                        {...field}
                        mode="dynamic"
                        label={"Date d'expiration"}
                        disabledPastDates
                        disabled={disabled}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </>
          )}
        </Box>
        <Typography variant={'label-semibold'} className={'mt-4'}>
          Merci de bien vérifier les informations saisies. Vous ne pourrez plus modifier ces
          informations une fois le formulaire validé.
        </Typography>

        <div className="flex justify-end mt-4 gap-2.5 mobile:flex-col mobile:gap-2">
          {/* <Button
            label="Enregistrer et continuer plus tard"
            variant="ghost"
            type="button"
            loading={loading['save-for-later']}
            onClick={() => {
              submitForm('save-for-later')
            }}
          /> */}
          <Button
            label="Valider et envoyer à LEC"
            variant="primary"
            type="button"
            loading={loading.submit}
            onClick={() => {
              submitForm('submit')
            }}
          />
        </div>
      </form>
    </Form>
  )
}
